/** @jsxImportSource @emotion/react */
import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as snackbarActions from "../../../../actions/snackbarActions";
import PageTitle from "../../../Utils/smsTitle";
import { SubHeader } from "../../../../Containers/SubHeader";
import MainSnackbar from "../../../Utils/Snackbar/SmsSnackbar";
import ObGynSidebar from "../../Utils/ObGynSidebar";
import ObGynPatientInfo from "../../Utils/ObGynPatientInfo";
import { useLabourAdmission } from "./hooks/useLabourAdmission";
import { LabourAdmissionForm } from "./LabourAdmissionForm";
import { LabourRiskAssessment } from "./LabourRiskAssessment";
import { LabourWardBoard } from "./LabourWardBoard";
import { history } from "../../../../Helpers/history";
import {
    LWPageWrapper,
    LWHeader,
    LWHeaderTitle,
    LWHeaderStats,
    StatBadge,

    AlertBanner,
    SectionNav,
    SectionBtn,
    ANCStrip,
    UnbookedBanner,
    RiskLevelBadge,
    LoadingWrapper,
    SaveBtn,
} from "./LabourWardStyles";
import { ObGynTabBar, ObGynTabBtn, ObGynTabBadge } from "../../Utils/ObGynTabStyles";

/**
 * LabourWardPage — Page-level container for Labour Ward Admission
 *
 * Route: /gyn-and-obs-labour-ward/:patient_number/:visit_id/:patient_type
 */
const LabourWardPage = function(props) {
    const actions = props.actions;
    const snackbar = props.snackbar;
    const params = props.match.params;
    const patient_number = params.patient_number;
    const visit_id = params.visit_id;
    const patient_type = params.patient_type;

    /* ─── Current user ─── */
    const user = JSON.parse(sessionStorage.getItem("user"));
    const userObj = !user ? {} : user;
    const token = !userObj.token ? [] : userObj.token;
    const _token = !token[1] ? {} : token[1];
    const doctor_name = (!_token.first_name ? "" : _token.first_name) + " " +
        (!_token.last_name ? "" : _token.last_name);

    /* ─── Labour Admission hook ─── */
    const lw = useLabourAdmission(actions, patient_number, visit_id);

    /* ─── Snackbar ─── */
    const closeSnackbar = function() { actions.snackbarActions.hideSnackbar(); };

    /* ─── Navigate to ANC booking ─── */
    const goToBooking = function() {
        history.push(
            "/gyn-and-obs-antenatal-card/" + patient_number + "/" + visit_id + "/" + patient_type
        );
    };

    /* ─── Navigate to existing admission module ─── */
    const goToGeneralAdmission = function() {
        history.push(
            "/gyn-and-obs-admission/" + patient_number + "/" + visit_id + "/" + patient_type
        );
    };

    /* ─── Section nav items ─── */
    const sectionItems = [
        { key: "admission", label: "🏥 Admission Details" },
        { key: "vitals", label: "🩺 Vitals & Exam" },
        { key: "obstetric", label: "🤰 Obstetric Exam" },
    ];

    /* ─── Risk level labels ─── */
    const riskLabels = {
        critical: "🔴 Critical",
        high: "🟠 High",
        medium: "🟡 Medium",
        low: "🟢 Low",
    };

    return (
        <div className="journals">
            <PageTitle title="Labour Ward Admission" />
            <SubHeader
                title="OB/GYN"
                subTitle="Labour Ward Admission"
                doctor_name={doctor_name}
            >
                <div className="header-icon-margin-right-zero">
                    <img
                        src="/images/antenatal-blue.png"
                        className="icon-resize surgery-icon-tabs ante-natal-green"
                        alt="Labour"
                    />
                </div>
            </SubHeader>
            <MainSnackbar
                variant={snackbar.type}
                open={snackbar.openBar}
                message={snackbar.message}
                handleCloseBar={closeSnackbar}
            />

            <ObGynSidebar
                patient_number={patient_number}
                visit_id={visit_id}
                patient_type={patient_type}
            >
                <LWPageWrapper>
                    {/* ═══ PATIENT INFO ═══ */}
                    <ObGynPatientInfo
                        patient_number={patient_number}
                        visit_id={visit_id}
                    />

                    {/* ═══ LOADING STATE ═══ */}
                    {lw.loadingBooking && (
                        <LoadingWrapper>
                            <span>⏳</span> Loading patient data...
                        </LoadingWrapper>
                    )}

                    {/* ═══ MAIN CONTENT (works for both booked and unbooked) ═══ */}
                    {!lw.loadingBooking && (
                        <div style={{ marginTop: "16px" }}>
                            {/* ─── Header ─── */}
                            <LWHeader>
                                <LWHeaderTitle>
                                    <h3>🏥 Labour Ward Admission</h3>
                                    <p>
                                        Obstetric admission, initial examination & risk assessment
                                        {lw.gestationalAge
                                            ? " · GA: " + lw.gestationalAge.display + " weeks"
                                            : ""}
                                    </p>
                                </LWHeaderTitle>
                                <LWHeaderStats>
                                    <StatBadge>
                                        <RiskLevelBadge level={lw.riskLevel} style={{
                                            padding: "3px 10px", fontSize: "0.74rem",
                                        }}>
                                            {riskLabels[lw.riskLevel] || "Low"} Risk
                                        </RiskLevelBadge>
                                    </StatBadge>
                                    {lw.riskFactors.length > 0 && (
                                        <StatBadge style={{ background: "rgba(229,57,53,0.3)" }}>
                                            ⚡ Risks <span className="count">{lw.riskFactors.length}</span>
                                        </StatBadge>
                                    )}
                                    {lw.bookingData && (
                                        <StatBadge>
                                            📋 ANC <span className="count">Booked</span>
                                        </StatBadge>
                                    )}
                                    {lw.admissionRecord && (
                                        <StatBadge style={{ background: "rgba(46,125,50,0.3)" }}>
                                            ✅ <span className="count">Admitted</span>
                                        </StatBadge>
                                    )}
                                </LWHeaderStats>
                            </LWHeader>

                            {/* ─── ANC Summary Strip (if booked) ─── */}
                            {lw.ancSummary && (
                                <ANCStrip>
                                    <span className="anc-label">📋 ANC Data Auto-Populated</span>
                                    <div className="anc-item">
                                        <span className="val">{lw.ancSummary.ancNumber || "—"}</span>
                                        <span className="lbl">ANC #</span>
                                    </div>
                                    <div className="anc-item">
                                        <span className="val">G{lw.ancSummary.gravida || "?"} P{lw.ancSummary.parity || "?"}</span>
                                        <span className="lbl">Gravida/Para</span>
                                    </div>
                                    <div className="anc-item">
                                        <span className="val">{lw.ancSummary.edd || "—"}</span>
                                        <span className="lbl">EDD</span>
                                    </div>
                                    <div className="anc-item">
                                        <span className="val">{lw.ancSummary.bloodGroup || "—"}</span>
                                        <span className="lbl">Blood Group</span>
                                    </div>
                                    <div className="anc-item">
                                        <span className="val">{lw.ancSummary.rhesus || "—"}</span>
                                        <span className="lbl">Rhesus</span>
                                    </div>
                                </ANCStrip>
                            )}

                            {/* ─── Unbooked patient banner ─── */}
                            {!lw.bookingData && (
                                <UnbookedBanner>
                                    <span className="ub-icon">⚠</span>
                                    <span className="ub-text">
                                        Unbooked patient — No ANC record found. Admission proceeds without ANC data.
                                    </span>
                                    <button className="ub-link" onClick={goToBooking}>
                                        Register ANC
                                    </button>
                                </UnbookedBanner>
                            )}

                            {/* ─── Tab Bar: Admission Form vs Labour Ward Board ─── */}
                            <ObGynTabBar>
                                <ObGynTabBtn
                                    active={lw.activeTab === "form"}
                                    onClick={function() { lw.setActiveTab("form"); }}
                                >
                                    📝 Admission Form
                                </ObGynTabBtn>
                                <ObGynTabBtn
                                    active={lw.activeTab === "risk"}
                                    onClick={function() { lw.setActiveTab("risk"); }}
                                >
                                    ⚡ Risk Assessment
                                    {lw.riskFactors.length > 0 && (
                                        <ObGynTabBadge active={lw.activeTab === "risk"}>{lw.riskFactors.length}</ObGynTabBadge>
                                    )}
                                </ObGynTabBtn>
                                <ObGynTabBtn
                                    active={lw.activeTab === "board"}
                                    onClick={function() { lw.setActiveTab("board"); }}
                                >
                                    🏥 Ward Board
                                    <ObGynTabBadge active={lw.activeTab === "board"}>{lw.boardStats.total}</ObGynTabBadge>
                                </ObGynTabBtn>
                            </ObGynTabBar>

                            {/* ─── Alert Banners ─── */}
                            {lw.activeTab === "form" && lw.alerts.map(function(alert, idx) {
                                return (
                                    <AlertBanner key={idx} type={alert.type}>
                                        <span className="alert-icon">{alert.icon}</span>
                                        <span className="alert-msg">{alert.msg}</span>
                                    </AlertBanner>
                                );
                            })}

                            {/* ─── Admission Form Tab ─── */}
                            {lw.activeTab === "form" && (
                                <div>
                                    {/* Section Navigation */}
                                    <SectionNav>
                                        {sectionItems.map(function(item) {
                                            return (
                                                <SectionBtn
                                                    key={item.key}
                                                    active={lw.activeSection === item.key}
                                                    onClick={function() { lw.setActiveSection(item.key); }}
                                                >
                                                    <span className="nav-dot" />
                                                    {item.label}
                                                </SectionBtn>
                                            );
                                        })}
                                    </SectionNav>

                                    <LabourAdmissionForm
                                        formData={lw.formData}
                                        handleChange={lw.handleChange}
                                        activeSection={lw.activeSection}
                                        saveAdmission={lw.saveAdmission}
                                        saving={lw.saving}
                                        constants={{
                                            REFERRAL_SOURCES: lw.REFERRAL_SOURCES,
                                            ONSET_TYPES: lw.ONSET_TYPES,
                                            MEMBRANE_STATUS: lw.MEMBRANE_STATUS,
                                            FETAL_PRESENTATIONS: lw.FETAL_PRESENTATIONS,
                                            FETAL_POSITIONS: lw.FETAL_POSITIONS,
                                            FETAL_LIES: lw.FETAL_LIES,
                                        }}
                                    />

                                    {/* Link to existing general admission */}
                                    <div style={{
                                        textAlign: "center", padding: "12px 0",
                                        fontSize: "0.82rem", color: "#78909c",
                                    }}>
                                        Need to complete general hospital admission (guardian, insurance, bed)?{" "}
                                        <button
                                            onClick={goToGeneralAdmission}
                                            style={{
                                                border: "none", background: "none",
                                                color: "#1a237e", fontWeight: 600,
                                                cursor: "pointer", textDecoration: "underline",
                                                fontSize: "0.82rem",
                                            }}
                                        >
                                            Go to General Admission →
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* ─── Risk Assessment Tab ─── */}
                            {lw.activeTab === "risk" && (
                                <div>
                                    <LabourRiskAssessment
                                        riskFactors={lw.riskFactors}
                                        toggleRiskFactor={lw.toggleRiskFactor}
                                        riskLevel={lw.riskLevel}
                                        RISK_FACTORS={lw.RISK_FACTORS}
                                    />
                                    <div style={{
                                        display: "flex", justifyContent: "flex-end",
                                        padding: "0 0 24px",
                                    }}>
                                        <SaveBtn
                                            disabled={lw.saving}
                                            onClick={lw.saveAdmission}
                                        >
                                            {lw.saving ? "Saving..." : "💾 Save Risk Assessment"}
                                        </SaveBtn>
                                    </div>
                                </div>
                            )}

                            {/* ─── Ward Board Tab ─── */}
                            {lw.activeTab === "board" && (
                                <LabourWardBoard
                                    admissions={lw.admissions}
                                    loadingBoard={lw.loadingBoard}
                                    boardStats={lw.boardStats}
                                    updateStatus={lw.updateStatus}
                                />
                            )}
                        </div>
                    )}
                </LWPageWrapper>
            </ObGynSidebar>
        </div>
    );
};

function mapStateToProps(state) {
    return { snackbar: state.snackbar };
}

function mapDispatchToProps(dispatch) {
    return {
        actions: {
            snackbarActions: bindActionCreators(snackbarActions, dispatch),
        },
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(LabourWardPage);
